import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type CompanyHoliday } from '../../lib/api.js';
import { useAuth } from '../../lib/auth-store.js';
import { Button } from '../ui/button.js';
import { Feedback } from '../ui/feedback.js';
import { Input } from '../ui/input.js';

/**
 * Admin → Company holidays. Days nobody works — statutory holidays, a
 * shutdown week. The board shades every tech on those days and the
 * date picker says how many are out. Adding a date that exists renames it.
 */
export function HolidaysPanel() {
  const role = useAuth((s) => s.user?.role);
  const canEdit = role === 'admin' || role === 'dispatcher';
  const queryClient = useQueryClient();
  const year = new Date().getFullYear();
  const query = useQuery({
    queryKey: ['admin', 'holidays'],
    queryFn: ({ signal }) => api.admin.techs.holidays({ from: `${year}-01-01` }, signal),
  });
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'holidays'] });
    void queryClient.invalidateQueries({ queryKey: ['availability'] });
  };
  const set = useMutation<CompanyHoliday, ApiError, CompanyHoliday>({
    mutationFn: (body) => api.admin.techs.setHoliday(body),
    onSuccess: () => {
      setDate('');
      setName('');
      invalidate();
    },
  });
  const remove = useMutation<void, ApiError, string>({
    mutationFn: (d) => api.admin.techs.removeHoliday(d),
    onSuccess: invalidate,
  });
  const [date, setDate] = useState('');
  const [name, setName] = useState('');

  return (
    <section
      className="rounded-xl border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
      aria-labelledby="holidays-title"
    >
      <h2 id="holidays-title" className="font-medium text-ink">
        Company holidays
      </h2>
      <p className="text-xs text-ink-muted mt-0.5">
        Days nobody works. Every tech is shown as out on the board; dropping a job on them still
        works but warns first. Individual days off are set on each tech (Skills matrix → Edit).
      </p>

      <ul className="mt-3 divide-y text-sm" style={{ borderColor: 'var(--color-border-tertiary)' }}>
        {(query.data ?? []).map((h) => (
          <li key={h.date} className="flex items-center gap-3 py-1.5">
            <span className="font-mono text-xs text-ink w-24">{h.date}</span>
            <span className="text-ink flex-1 truncate">{h.name}</span>
            {canEdit && (
              <button
                type="button"
                onClick={() => remove.mutate(h.date)}
                disabled={remove.isPending}
                className="text-xs text-ink-muted hover:text-ink"
              >
                Remove
              </button>
            )}
          </li>
        ))}
        {query.isSuccess && query.data.length === 0 && (
          <li className="py-2 text-xs text-ink-subtle">No holidays set for {year} onward.</li>
        )}
      </ul>

      {canEdit && (
        <div className="mt-3 grid grid-cols-[auto_1fr_auto] gap-2 items-center">
          <Input type="date" aria-label="Holiday date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 text-sm" />
          <Input aria-label="Holiday name" placeholder="e.g. Christmas Day" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className="h-10 text-sm" />
          <Button
            size="sm"
            disabled={!date || !name.trim() || set.isPending}
            onClick={() => set.mutate({ date, name: name.trim() })}
          >
            {set.isPending ? 'Saving…' : 'Add'}
          </Button>
        </div>
      )}
      {(set.isError || remove.isError) && (
        <Feedback variant="error" size="sm" className="mt-2">
          {(set.error ?? remove.error)?.message}
        </Feedback>
      )}
    </section>
  );
}
